const STORAGE_KEY = "xltools-utm-params";

const UTM_PARAMS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_content",
  "utm_term",
  "utm_id",
];

const UTM_FIELD_NAMES = {
  utm_source: "source",
  utm_medium: "medium",
  utm_campaign: "campaign",
  utm_content: "content",
  utm_term: "term",
  utm_id: "id",
};

const UTM_FORM_DATA_FIELDS = {
  source: "Utms.Source",
  medium: "Utms.Medium",
  campaign: "Utms.Campaign",
  content: "Utms.Content",
  term: "Utms.Term",
  id: "Utms.Id",
};

const safeWindow = () => typeof window !== "undefined";

/**
 * Parse UTM parameters from the current URL (or a given search string).
 * Returns an object like { utm_source: "google", utm_medium: "cpc" }
 * or an empty object when no UTM params are present.
 */
export const parseUtmParams = (search) => {
  if (!search) {
    return {};
  }

  const params = new URLSearchParams(search);
  const utms = {};

  UTM_PARAMS.forEach((key) => {
    const value = params.get(key);

    if (value && value.trim().length > 0) {
      utms[key] = value.trim();
    }
  });

  return utms;
};

/**
 * Read the stored UTM data from localStorage.
 * Returns { first_utm_*: ..., last_utm_*: ... } or {}.
 */
export const getStoredUtms = () => {
  if (!safeWindow()) {
    return {};
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);

    if (!raw) {
      return {};
    }

    const parsed = JSON.parse(raw);

    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
};

/**
 * Persist UTM data to localStorage.
 */
const persistUtms = (data) => {
  if (!safeWindow()) {
    return;
  }

  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

/**
 * Check whether any first-touch UTMs have already been stored.
 */
const hasFirstTouch = (stored) =>
  Object.keys(stored).some((key) => key.startsWith("first_"));

/**
 * Build prefixed keys from raw UTM params.
 * e.g. { utm_source: "google" } -> { first_utm_source: "google" }
 */
const prefixUtms = (utms, prefix) => {
  const result = {};

  Object.entries(utms).forEach(([key, value]) => {
    result[`${prefix}${key}`] = value;
  });

  return result;
};

/**
 * Capture UTM parameters from the current page URL and store them
 * using first-touch / last-touch logic:
 *
 * - If no first-touch UTMs exist yet → store as first_utm_*
 * - If first-touch already exists   → update last_utm_*
 *
 * Call this on every page load / client-side navigation.
 */
export const captureUtmParams = (search) => {
  if (!safeWindow()) {
    return;
  }

  const currentSearch = search ?? window.location.search;
  const utms = parseUtmParams(currentSearch);

  if (Object.keys(utms).length === 0) {
    return;
  }

  const stored = getStoredUtms();

  if (!hasFirstTouch(stored)) {
    // First visit with UTM params - store as first-touch
    const firstTouch = prefixUtms(utms, "first_");
    persistUtms({ ...stored, ...firstTouch });
  } else {
    // Subsequent visit with UTM params - update last-touch,
    // but also backfill any first-touch values that are missing
    // (e.g. first visit only had utm_source, now we also see utm_medium)
    const firstBackfill = {};
    Object.entries(utms).forEach(([key, value]) => {
      const firstKey = `first_${key}`;
      if (!(firstKey in stored)) {
        firstBackfill[firstKey] = value;
      }
    });

    const lastTouch = prefixUtms(utms, "last_");
    persistUtms({ ...stored, ...firstBackfill, ...lastTouch });
  }

  stripUtmParams();
};

/**
 * Remove UTM parameters from the browser address bar without triggering
 * a page reload or navigation. Keeps all non-UTM query params intact.
 */
const stripUtmParams = () => {
  const url = new URL(window.location.href);
  let changed = false;

  UTM_PARAMS.forEach((key) => {
    if (url.searchParams.has(key)) {
      url.searchParams.delete(key);
      changed = true;
    }
  });

  if (changed) {
    window.history.replaceState(window.history.state, "", url.toString());
  }
};

const getBestUtmValue = (stored, key) =>
  stored[`last_${key}`] || stored[`first_${key}`] || "";

export const getResolvedUtms = () => {
  const stored = getStoredUtms();

  return UTM_PARAMS.reduce((result, key) => {
    const value = getBestUtmValue(stored, key);

    if (!value) {
      return result;
    }

    result[UTM_FIELD_NAMES[key]] = value;
    return result;
  }, {});
};

export const appendUtmData = (payload) => {
  const utms = getResolvedUtms();

  if (!payload || Object.keys(utms).length === 0) {
    return payload;
  }

  if (payload instanceof FormData) {
    Object.entries(UTM_FORM_DATA_FIELDS).forEach(([key, fieldName]) => {
      if (utms[key]) {
        payload.set(fieldName, utms[key]);
      }
    });

    return payload;
  }

  return {
    ...payload,
    utms: {
      ...(payload.utms || {}),
      ...utms,
    },
  };
};
